import React, { useEffect, useCallback, ReactNode, useRef } from 'react';
import { Platform } from 'react-native';
import { ClientErrorLevelEnum } from '@matap/shared';
import { useSocket } from '@/hooks/use.socket';
import { getDeviceProfile } from '@/webrtc/utils/utils';
import { record } from '@rrweb/record';
import { coreStore } from '../redux-store';

interface ProviderProps {
  children: ReactNode;
}

const ignores = ["play() failed because the user didn't interact with the document first"];

function BaseErrorReporterProvider({ children }: ProviderProps) {
  const { sendClientError } = useSocket();

  // THE FIX 1: Change this to a 2D array (Matrix)
  const eventsMatrixRef = useRef<any[][]>([[]]);

  const send = useCallback(
    (level: ClientErrorLevelEnum, error: Error, metadata?: Record<string, any>) => {
      sendClientError({
        message: error.message || String(error),
        stack: error.stack,
        url: Platform.OS === 'web' ? window.location.href : undefined,
        userAgent: Platform.OS,
        level,
        metadata: {
          ...metadata,
          visitId: coreStore.getState().userReducer.state?.visit?._id,
          deviceProfile: getDeviceProfile(),
        },
        // THE FIX 2: Flatten the matrix into a single array before sending to MongoDB
        replayData: Platform.OS === 'web' ? eventsMatrixRef.current.flat() : undefined,
      });
    },
    [sendClientError],
  );

  useEffect(() => {
    let stopRecording: (() => void) | undefined;

    if (Platform.OS === 'web') {
      stopRecording = record({
        emit(event, isCheckout) {
          if (isCheckout) {
            eventsMatrixRef.current.push([]);

            if (eventsMatrixRef.current.length > 2) {
              eventsMatrixRef.current.shift();
            }
          }
          eventsMatrixRef.current[eventsMatrixRef.current.length - 1].push(event);
        },
        ignoreSelector: '[data-testid="rrweb-ignore"]',
        blockSelector: 'canvas, [data-testid="rrweb-block"]',
        checkoutEveryNms: 10000,
        sampling: {
          mouseInteraction: true,
          scroll: 150,
          mousemove: false,
          media: 800,
        },
      });

      const errorHandler = (event: ErrorEvent) => {
        if (__DEV__) {
          return;
        }
        const errorObj = event.error || new Error(event.message);
        if (ignores.find((i) => event.message?.includes(i))) return;

        send(ClientErrorLevelEnum.ERROR, errorObj, {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
        });
      };

      const rejectionHandler = (event: PromiseRejectionEvent) => {
        if (__DEV__) {
          return;
        }
        const error =
          event.reason instanceof Error
            ? event.reason
            : new Error(
                typeof event.reason === 'object' && event.reason?.constructor?.name ? `${event.reason.constructor.name}: ${String(event.reason)}` : String(event.reason),
              );
        if (ignores.find((i) => event.reason?.includes?.(i))) return;

        send(ClientErrorLevelEnum.CRITICAL, error, {
          reason: event.reason,
        });
      };

      window.addEventListener('error', errorHandler);
      window.addEventListener('unhandledrejection', rejectionHandler);

      return () => {
        window.removeEventListener('error', errorHandler);
        window.removeEventListener('unhandledrejection', rejectionHandler);
        if (stopRecording) stopRecording();
      };
    } else {
      const defaultErrorHandler = ErrorUtils.getGlobalHandler();

      ErrorUtils.setGlobalHandler((error: any, isFatal: boolean) => {
        if (__DEV__) {
          defaultErrorHandler(error, isFatal);
        } else {
          if (ignores.find((i) => error.message?.includes(i))) return;
          send(isFatal ? ClientErrorLevelEnum.CRITICAL : ClientErrorLevelEnum.ERROR, error, { message: error.message, stack: error.stack });
        }
      });
    }
  }, [send]);

  return <>{children}</>;
}

export const ErrorReporterProvider = React.memo(BaseErrorReporterProvider);
